import { Component, OnInit } from '@angular/core';
import { environment } from '@app/env';
import { AnalyticsService } from '@app/shared/modules/analytics/services/analytics.service';
import { CookieUtilsService } from '@app/shared/utils/cookie-utils.service';
import { BehaviorSubject } from 'rxjs';
import { filter, startWith, switchMap } from 'rxjs/operators';
import { AnalyticsHelperService } from 'src/app/modules/evc-small-biz/services/analytics-helper.service';
import {
  ChargePointLocation,
  ChargePointType,
  ChargingStationConfig,
} from 'src/app/modules/evc-small-biz/stores/small-biz-store.model';
import { fadeInUp } from '../../../animations/fly-animations';
import { IModalNavigator } from '../../../component-lib/components/modal/navigator/modal-navigator.interface';
import { IMultiModalComponent } from '../../../component-lib/components/modal/navigator/multi-modal-component.interface';
import { ButtonStateEnum } from '../../../component-lib/enums/button-state.enum';
import { isOneOf } from '../../../helpers/is-one-of-enum';
import { ChargingStationPricing } from '../charging-station-pricing.service';
import { ChargingIntroductionComponent } from '../../introduction/introduction.component';
import { IntroductionType } from '../../introduction/introduction.model';
import { ChargingStationOutboundInfoComponent } from '../outbound/info/info.component';
import { ChargingStationInputService } from './input.service';

@Component({
  selector: 'evsb-charging-station-input',
  templateUrl: './input.component.html',
  animations: [fadeInUp('200ms')],
})
export class ChargingStationInputComponent implements OnInit, IMultiModalComponent {
  public inputModel: ChargingStationConfig;
  public modalNavigator: IModalNavigator;
  public canGoBack = true;

  public maxStations = environment.maxChargingStations;

  public desiredSolution$ = new BehaviorSubject<ChargingStationConfig>(null);
  public solutionPrice$ = this.desiredSolution$.pipe(
    filter((solution) => !!solution),
    switchMap((solution) => this.pricing.for$(solution)),
    startWith(0)
  );

  constructor(
    private pageService: ChargingStationInputService,
    private pricing: ChargingStationPricing,
    private analyticsService: AnalyticsService,
    public analyticsHelperService: AnalyticsHelperService,
    private cookieUtilsService: CookieUtilsService
  ) {}

  ngOnInit(): void {
    this.inputModel = this.pageService.getInputModel();
    this.canGoBack = this.cookieUtilsService.getCookie(IntroductionType.skipIntroChargingStation) === 'true';
    this.analytics();
  }

  public calculateSolutionPrice(solution: ChargingStationConfig) {
    this.desiredSolution$.next(solution);
    this.inputModel = solution;

    if (this.inputModel.amount > this.maxStations) {
      this.navigateToOutbound();
    }
  }

  public add() {
    if (!this.hasCompletedFlow) {
      return;
    }

    this.analyticsAdd();
    this.pageService.add(this.inputModel);

    this.analyticsAdded();
    this.modalNavigator.close();
  }

  public goBack(): void {
    this.cookieUtilsService.setCookie(IntroductionType.skipIntroChargingStation, false);

    const modalInstance = this.modalNavigator.goToView(ChargingIntroductionComponent) as ChargingIntroductionComponent;
    modalInstance.data = { introductionType: IntroductionType.skipIntroChargingStation };
  }

  public get hasCompletedFlow() {
    return this.wantsAtLeastOneChargingStation && this.hasChosenChargingSpeed && this.hasChosenPlacement;
  }

  public get submitBtnState(): ButtonStateEnum {
    return this.hasCompletedFlow ? ButtonStateEnum.enabled : ButtonStateEnum.disabled;
  }

  public get hasChosenChargingSpeed(): boolean {
    return isOneOf(ChargePointType, this.inputModel.type);
  }

  public get wantsAtLeastOneChargingStation(): boolean {
    return this.inputModel.amount >= 1;
  }

  public get hasChosenPlacement(): boolean {
    return isOneOf(ChargePointLocation, this.inputModel.location);
  }

  private navigateToOutbound(): void {
    const modalInstance = this.modalNavigator.goToView(
      ChargingStationOutboundInfoComponent
    ) as ChargingStationOutboundInfoComponent;
  }

  private analytics(): void {
    this.analyticsService.push({
      event: 'b-to-b-ev-simulator-flow',
      eventCategory: 'b-to-b-ev-simulator',
      eventAction: 'step-charging-station',
      eventLabel: 'modify-quantity',
    });
  }

  private analyticsAdd(): void {
    this.analyticsService.push({
      event: 'cta',
      eventCategory: 'b-to-b-ev-simulator',
      eventAction: 'step-charging-station',
      eventLabel: 'add-solution-charging-station',
    });
  }

  private analyticsAdded(): void {
    this.analyticsHelperService.analyticsWorkflowStep('overview/product-added');
  }
}
